import { HttpParams } from '@angular/common/http';
import {ChangeDetectionStrategy, Component, forwardRef, Input, OnDestroy, TemplateRef} from '@angular/core';
import {ControlValueAccessor, NG_ASYNC_VALIDATORS, NG_VALUE_ACCESSOR, UntypedFormControl} from '@angular/forms';
import {Observable, Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/internal/operators';
import {Account} from '../../models/Account/account.object';
import {AccountService} from '../../models/Account/account.service';
import {FilterObject} from '../../objects/filter-object';

@Component({
    selector: 'app-account-selector',
    templateUrl: './selector.component.html',
    providers: [
        AccountService,
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AccountSelectorComponent), multi: true },
        { provide: NG_ASYNC_VALIDATORS, useExisting: forwardRef(() => AccountSelectorComponent), multi: true }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AccountSelectorComponent implements ControlValueAccessor, OnDestroy {

  // SELECTOR <=> this.ChildControl <=> CONTROLLER (THIS) <=> value (get/set) <=> END component

  Results: Account[];
  ChildControl = new UntypedFormControl();

  @Input('name') name: string = null; // verplicht ?
  @Input() placeholder = 'Account username';
  @Input() disabled: false;
  @Input() required: any = null; // Does not work with @param required
  @Input() distanceTo: { lat: number, lng: number } = null; // If isset, calculate the distance to this point
  @Input() matSuffix: TemplateRef<any> = null; // Wrap into matSuffix
  @Input() filter: FilterObject = new FilterObject();
  Selected: Account = null; // Default selected
  filterObject: FilterObject = new FilterObject;

  Subscription: Subscription;
  // CODE BELOW IS REQUIRED TO REGISTER THE NG_VALUE_ACCESSOR
  onChange: any = () => {
  };
  onTouched: any = () => {
  }

  constructor(private _Account: AccountService) {
    this.filterObject.include = ['Address'];
    this.Subscription = this.ChildControl.valueChanges
        .pipe(debounceTime(400))
        .subscribe(a => {
          if (a instanceof Account) { // Selected trough autocomplete
            this.value = a;
          } else if (a) { // String
            this.doSearch(a).subscribe(res => {
              this.Results = res;
              // Only one result found / multiple
              this.value = res && res.length === 1 ? res[0] : a;
            });
          } else { // Empty
            this.value = null;
          }
        });
  }

  get value() {
    return this.Selected instanceof Account ? this.Selected : null;
  }

  set value(val) {
    this.Selected = val;
    if (val !== this.ChildControl.value) this.ChildControl.setValue(val);
    this.onChange(this.Selected);
    this.onTouched();
  }

  // Async validator, note the NG_ASYNC_VALIDATORS provider !
  ngOnDestroy() {
    this.Subscription.unsubscribe();
  }

  // Triggered when param value changes.
  validate(c: UntypedFormControl): Observable<any> {
    return new Observable(observer => {
      // Selected from autocomplete list = valid OR empty and NOT required
      if (this.Selected instanceof Account || (!this.ChildControl.value && !this.required)) {
        this.ChildControl.setErrors(null);
        observer.next(null);
        observer.complete();
      } else {
        this.ChildControl.setErrors({err: 'err'});
        observer.next({'err': true});
        observer.complete();
      }
    });
  }

  // Asynch search observable
  doSearch(e: any): Observable<Account[]> {
    this.filterObject.where = {'or': [{'Email': {'like': '%' + e + '%'}}, {'Fullname': {'like': '%' + e + '%'}}]};
    if (this.filter.where) this.filterObject.where = {and: [this.filterObject.where, this.filter.where]};
    const Params = new HttpParams().append('filter', JSON.stringify(this.filterObject));
    return this._Account.search(Params);
  }

  displayFn(input: Account): any {
    return input instanceof Account ? input.Email : input;
  }

  registerOnChange(fn) {
    this.onChange = fn;
  }

  registerOnTouched(fn) {
    this.onTouched = fn;
  }

  writeValue(value) {
    this.value = value;
  }

}
